import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { BankStatementRuleConditionFormInterface } from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-condition-form.dto';
import type { BankStatementRuleExpressionFormInterface } from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-expression-form.dto';
import type { BankStatementRuleFindInterface } from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-find.dto';
import { BankStatementRuleFormDto, type BankStatementRuleFormInterface } from '@zaoblako/shared/modules/document/dtos/bank-statement-rule/bank-statement-rule-form.dto';
import { BankStatementRuleConditionKindEnum } from '@zaoblako/shared/modules/document/enums/bank.statement.rule.condition.kind.enum';
import { BankStatementRuleExpressionTypeEnum } from '@zaoblako/shared/modules/document/enums/bank.statement.rule.expression.type.enum';
import { BasisClientService } from '@zaoblako/web/core/services/basis/basis-client.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DocumentRegulationService } from '@zaoblako/web/core/services/document/document-regulation.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BankDetailsAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/bank-details-auto-complete/bank-details-auto-complete.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { BankStatementRuleConditionKindSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/bank-statement-rule-condition-kind-single-select/bank-statement-rule-condition-kind-single-select.component';
import { BankStatementRuleConditionTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/bank-statement-rule-condition-type-single-select/bank-statement-rule-condition-type-single-select.component';
import { BankStatementRuleJointTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/bank-statement-rule-joint-type-single-select/bank-statement-rule-joint-type-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-regulation-form',
  templateUrl: './document-regulation-form.component.html',
  imports: [
    CommonModule,
    ButtonModule,
    InputTextComponent,
    JoinPipe,
    CardModule,
    BankStatementRuleConditionKindSingleSelectComponent,
    BankStatementRuleConditionTypeSingleSelectComponent,
    SingleSelectComponent,
    BankDetailsAutoCompleteComponent,
    ContragentAutoCompleteComponent,
    DatepickerComponent,
    InputNumberModule,
    FormsModule,
    SkeletonModule,
    DividerModule,
    BankStatementRuleJointTypeSingleSelectComponent,
    CheckboxModule,
    MultiSelectComponent,
    SelectButtonModule,
    DirectionSingleSelectComponent,
    SpinnerComponent,
    DealAutoCompleteComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentRegulationFormComponent extends BaseFormComponent<BankStatementRuleFormInterface> implements OnInit {
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public readonly index: number;
  public regulation = <BankStatementRuleFormInterface>{};
  public dto = BankStatementRuleFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public companies: CompanyFindInterface[] = [];
  public contragents: ContragentFindInterface[] = [];
  public bankDetails: BankDetailsFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];

  public excludeOptions = [
    { label: 'Загружать', value: false },
    { label: 'Не загружать', value: true }
  ];

  public get BankStatementRuleConditionKindEnum() {
    return BankStatementRuleConditionKindEnum;
  }

  public get BankStatementRuleExpressionTypeEnum() {
    return BankStatementRuleExpressionTypeEnum;
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly documentRegulationService: DocumentRegulationService,
    private readonly companyService: CompanyService,
    private readonly contragentService: ContragentService,
    private readonly basisClientService: BasisClientService,
    private readonly directionService: DirectionService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.companyService.find<CompanyFindInterface>(),
      this.directionService.find<DirectionFindInterface>()
    ]).subscribe(([companies, directions]) => {
      this.companies = companies;
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.regulation.exclude = false;
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.documentRegulationService
      .findById<BankStatementRuleFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (regulation) => {
          this.regulation = regulation as any;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveRegulation() {
    if (this.checkValidate(this.regulation).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.documentRegulationService
          .update<BankStatementRuleFormInterface, BankStatementRuleFindInterface>(this.regulation)
          .subscribe({
            next: (regulation) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Правило "${regulation?.name}" изменено`);
              this.formStoreService.saveNext(regulation, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.documentRegulationService.create<BankStatementRuleFormInterface>(this.regulation).subscribe({
          next: (regulation) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Правило создано');
            this.formStoreService.saveNext(regulation, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteRegulation() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить правило "${this.regulation.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.documentRegulationService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Правило "${this.regulation?.name}" удалено`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public searchContragent(name: string) {
    this.contragentService.search({ search: name }).subscribe((contragents) => {
      this.contragents = contragents;
      this.cdRef.markForCheck();
    });
  }

  public searchBankDetails(name: string) {
    this.basisClientService.bankDetailsSearch({ search: name }).subscribe((bankDetails) => {
      this.bankDetails = bankDetails;
      this.cdRef.markForCheck();
    });
  }

  public addExpression() {
    if (!this.regulation.expressions) {
      this.regulation.expressions = [];
    }

    if (this.regulation.expressions.length) {
      const expressionOperator: BankStatementRuleExpressionFormInterface = {
        type: BankStatementRuleExpressionTypeEnum.OPERATOR
      };
      this.regulation.expressions.push(expressionOperator);
    }

    const expressionCondition: BankStatementRuleExpressionFormInterface = {
      condition: <BankStatementRuleConditionFormInterface>{},
      type: BankStatementRuleExpressionTypeEnum.CONDITION
    };
    this.regulation.expressions.push(expressionCondition);
  }

  public deleteExpression(expressionCondition: BankStatementRuleExpressionFormInterface, index: number) {
    let expressionOperator: BankStatementRuleExpressionFormInterface;
    if (index !== this.regulation.expressions?.length - 1) {
      expressionOperator = this.regulation.expressions[index + 1];
    } else {
      expressionOperator = this.regulation.expressions[index - 1];
    }
    this.regulation.expressions = this.regulation.expressions.filter((_expression) => {
      return _expression !== expressionCondition && _expression !== expressionOperator;
    });
    this.regulation.expressions = [...this.regulation.expressions];
  }
}
